// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  SimpleGrid,
  Divider,
  Table,
  Loader,
  Center,
  Title,
  Box,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DateInput } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconSend,
  IconCurrencyDollar,
  IconX,
  IconPrinter,
  IconArrowLeft,
} from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';
import {
  useInvoice,
  useInvoicePayments,
  useSendInvoice,
  useRecordPayment,
  useVoidInvoice,
} from '../../hooks/useInvoicing';
import { formatCurrency, formatDate } from '../../utils/format';
import type { PaymentMethod, InvoiceLineItem, Payment } from '../../types/invoice';

const INVOICE_STATUS_COLORS: Record<string, string> = {
  draft: 'gray',
  sent: 'blue',
  viewed: 'cyan',
  partial: 'yellow',
  paid: 'green',
  overdue: 'red',
  cancelled: 'gray',
  written_off: 'orange',
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  check: 'Check',
  ach: 'ACH',
  wire: 'Wire Transfer',
  credit_card: 'Credit Card',
  other: 'Other',
};

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: invoice, loading } = useInvoice(id ?? '');
  const { data: rawPayments } = useInvoicePayments(id ?? '');
  const payments = rawPayments as any;

  const sendInvoiceMut = useSendInvoice();
  const recordPaymentMut = useRecordPayment();
  const voidInvoiceMut = useVoidInvoice();

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [voidModalOpen, setVoidModalOpen] = useState(false);

  // Payment form
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentDate, setPaymentDate] = useState<string | null>(new Date().toISOString().split('T')[0] ?? null);
  const [paymentMethod, setPaymentMethod] = useState<string>('ach');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  // Void form
  const [voidReason, setVoidReason] = useState('');

  if (loading) {
    return <Center py="xl"><Loader /></Center>;
  }

  if (!invoice) {
    return (
      <Center py="xl">
        <Text c="dimmed">Invoice not found.</Text>
      </Center>
    );
  }

  const openPaymentModal = () => {
    setPaymentAmount(invoice.amountDue);
    setPaymentDate(new Date().toISOString().split('T')[0] ?? null);
    setPaymentMethod('ach');
    setPaymentReference('');
    setPaymentNotes('');
    setPaymentModalOpen(true);
  };

  const handleSend = async () => {
    try {
      await sendInvoiceMut.mutateAsync(invoice.id);
      notifications.show({ title: 'Invoice Sent', message: `Invoice ${invoice.invoiceNumber} has been sent.`, color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to send invoice.', color: 'red' });
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentDate || paymentAmount <= 0) return;
    try {
      await recordPaymentMut.mutateAsync({
        invoiceId: invoice.id,
        amount: paymentAmount,
        paymentDate: paymentDate ?? '',
        method: paymentMethod as PaymentMethod,
        reference: paymentReference || undefined,
        notes: paymentNotes || undefined,
      });
      notifications.show({ title: 'Payment Recorded', message: `Payment of ${formatCurrency(paymentAmount)} recorded.`, color: 'green' });
      setPaymentModalOpen(false);
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to record payment.', color: 'red' });
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) return;
    try {
      await voidInvoiceMut.mutateAsync({ id: invoice.id, reason: voidReason });
      notifications.show({ title: 'Invoice Voided', message: `Invoice ${invoice.invoiceNumber} has been voided.`, color: 'orange' });
      setVoidModalOpen(false);
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to void invoice.', color: 'red' });
    }
  };

  const canSend = invoice.status === 'draft';
  const canRecordPayment = ['sent', 'viewed', 'partial', 'overdue'].includes(invoice.status);
  const canVoid = !['paid', 'cancelled', 'written_off'].includes(invoice.status);

  return (
    <Stack gap="md">
      <PageHeader
        title={`Invoice ${invoice.invoiceNumber}`}
        breadcrumbs={[
          { label: 'Invoices', to: '/admin/workforce/invoicing' },
          { label: invoice.invoiceNumber },
        ]}
        actions={
          <Group gap="sm">
            <Button
              variant="subtle"
              icon={<IconArrowLeft size={16} />}
              onClick={() => navigate('/admin/workforce/invoicing')}
            >
              Back
            </Button>
            {canSend && (
              <Button
                color="teal"
                icon={<IconSend size={16} />}
                onClick={handleSend}
                loading={sendInvoiceMut.isPending}
              >
                Send Invoice
              </Button>
            )}
            {canRecordPayment && (
              <Button
                color="green"
                icon={<IconCurrencyDollar size={16} />}
                onClick={openPaymentModal}
              >
                Record Payment
              </Button>
            )}
            {canVoid && (
              <Button
                color="red"
                variant="outline"
                icon={<IconX size={16} />}
                onClick={() => { setVoidReason(''); setVoidModalOpen(true); }}
              >
                Void
              </Button>
            )}
            <Button
              variant="default"
              icon={<IconPrinter size={16} />}
              onClick={() => window.print()}
            >
              Print
            </Button>
          </Group>
        }
      />

      {/* Invoice Header */}
      <Paper withBorder p="lg" radius="md">
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Stack gap="xs">
            <Group gap="sm">
              <Text fw={700} size="lg">{invoice.invoiceNumber}</Text>
              <StatusBadge status={invoice.status} colorMap={INVOICE_STATUS_COLORS} size="md" />
            </Group>
            <Text size="sm" c="dimmed">Client</Text>
            <Text fw={600}>{invoice.clientName}</Text>
            {invoice.clientId && (
              <Text size="xs" c="dimmed">ID: {invoice.clientId}</Text>
            )}
          </Stack>

          <Stack gap="xs" ta="flex-end">
            <SimpleGrid cols={2} spacing="xs">
              <Text size="sm" c="dimmed" ta="right">Issue Date:</Text>
              <Text size="sm" fw={500}>{formatDate(invoice.issueDate)}</Text>
              <Text size="sm" c="dimmed" ta="right">Due Date:</Text>
              <Text size="sm" fw={500}>{formatDate(invoice.dueDate)}</Text>
              <Text size="sm" c="dimmed" ta="right">Currency:</Text>
              <Text size="sm" fw={500}>{invoice.currency}</Text>
            </SimpleGrid>
          </Stack>
        </SimpleGrid>
      </Paper>

      {/* Line Items */}
      <Paper withBorder p="lg" radius="md">
        <Title order={4} mb="md">Line Items</Title>
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Description</Table.Th>
              <Table.Th w={100} style={{ textAlign: 'right' }}>Qty</Table.Th>
              <Table.Th w={130} style={{ textAlign: 'right' }}>Unit Price</Table.Th>
              <Table.Th w={130} style={{ textAlign: 'right' }}>Amount</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {invoice.lineItems.map((item: InvoiceLineItem, idx: number) => (
              <Table.Tr key={idx}>
                <Table.Td>
                  <Text size="sm">{item.description}</Text>
                  {item.jobName && <Text size="xs" c="dimmed">Job: {item.jobName}</Text>}
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{item.quantity}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(item.unitPrice)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(item.amount)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        <Divider my="md" />

        <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Stack gap={4} w={280}>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Subtotal:</Text>
              <Text size="sm" fw={500}>{formatCurrency(invoice.subtotal)}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Tax ({invoice.taxRate}%):</Text>
              <Text size="sm" fw={500}>{formatCurrency(invoice.taxAmount)}</Text>
            </Group>
            <Divider />
            <Group justify="space-between">
              <Text fw={700}>Total:</Text>
              <Text fw={700} size="lg">{formatCurrency(invoice.total)}</Text>
            </Group>
            {invoice.amountPaid > 0 && (
              <Group justify="space-between">
                <Text size="sm" c="green">Paid:</Text>
                <Text size="sm" c="green" fw={500}>{formatCurrency(invoice.amountPaid)}</Text>
              </Group>
            )}
            {invoice.amountDue > 0 && invoice.amountDue !== invoice.total && (
              <Group justify="space-between">
                <Text fw={600} c="red">Amount Due:</Text>
                <Text fw={600} c="red">{formatCurrency(invoice.amountDue)}</Text>
              </Group>
            )}
          </Stack>
        </Box>
      </Paper>

      {/* Payment History */}
      <Paper withBorder p="lg" radius="md">
        <Title order={4} mb="md">Payment History</Title>
        {!payments || payments.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No payments recorded yet.
          </Text>
        ) : (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th>Method</Table.Th>
                <Table.Th>Reference</Table.Th>
                <Table.Th>Notes</Table.Th>
                <Table.Th w={130} style={{ textAlign: 'right' }}>Amount</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {payments.map((payment: Payment) => (
                <Table.Tr key={payment.id}>
                  <Table.Td>{formatDate(payment.paymentDate)}</Table.Td>
                  <Table.Td>{PAYMENT_METHOD_LABELS[payment.method] ?? payment.method}</Table.Td>
                  <Table.Td>{payment.reference || '-'}</Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={1}>{payment.notes || '-'}</Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <CurrencyDisplay amount={payment.amount} size="sm" />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {/* Notes & Terms */}
      {(invoice.notes || invoice.terms) && (
        <Paper withBorder p="lg" radius="md">
          {invoice.notes && (
            <>
              <Title order={5} mb="xs">Notes</Title>
              <Text size="sm" mb="md" style={{ whiteSpace: 'pre-wrap' }}>{invoice.notes}</Text>
            </>
          )}
          {invoice.terms && (
            <>
              <Title order={5} mb="xs">Payment Terms</Title>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{invoice.terms}</Text>
            </>
          )}
        </Paper>
      )}

      {/* Record Payment Modal */}
      <Modal
        opened={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        title={`Record Payment - ${invoice.invoiceNumber}`}
        size="md"
      >
        <Stack gap="md">
          <Paper withBorder p="sm" bg="gray.0">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Invoice Total:</Text>
              <Text size="sm" fw={500}>{formatCurrency(invoice.total)}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Amount Due:</Text>
              <Text size="sm" fw={700} c="red">{formatCurrency(invoice.amountDue)}</Text>
            </Group>
          </Paper>

          <NumberInput
            label="Payment Amount"
            value={paymentAmount}
            onChange={(val) => setPaymentAmount(Number(val) || 0)}
            min={0.01}
            max={invoice.amountDue}
            decimalScale={2}
            prefix="$"
            required
          />

          <DateInput
            label="Payment Date"
            value={paymentDate}
            onChange={setPaymentDate}
            required
          />

          <Select
            label="Payment Method"
            value={paymentMethod}
            onChange={(v) => setPaymentMethod(v ?? 'ach')}
            data={[
              { value: 'check', label: 'Check' },
              { value: 'ach', label: 'ACH' },
              { value: 'wire', label: 'Wire Transfer' },
              { value: 'credit_card', label: 'Credit Card' },
              { value: 'other', label: 'Other' },
            ]}
            required
          />

          <TextInput
            label="Reference Number"
            placeholder="Check number, transaction ID, etc."
            value={paymentReference}
            onChange={(e) => setPaymentReference(e.currentTarget.value)}
          />

          <Textarea
            label="Notes"
            placeholder="Optional payment notes"
            value={paymentNotes}
            onChange={(e) => setPaymentNotes(e.currentTarget.value)}
            rows={2}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setPaymentModalOpen(false)}>Cancel</Button>
            <Button
              color="green"
              onClick={handleRecordPayment}
              loading={recordPaymentMut.isPending}
              icon={<IconCurrencyDollar size={16} />}
            >
              Record Payment
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Void Invoice Modal */}
      <Modal
        opened={voidModalOpen}
        onClose={() => setVoidModalOpen(false)}
        title={`Void Invoice - ${invoice.invoiceNumber}`}
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            This action cannot be undone. The invoice will be marked as cancelled.
          </Text>

          <Textarea
            label="Reason for voiding"
            placeholder="Provide a reason for cancelling this invoice"
            value={voidReason}
            onChange={(e) => setVoidReason(e.currentTarget.value)}
            rows={3}
            required
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setVoidModalOpen(false)}>Cancel</Button>
            <Button
              color="red"
              onClick={handleVoid}
              loading={voidInvoiceMut.isPending}
              disabled={!voidReason.trim()}
            >
              Void Invoice
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
